import _ from 'lodash'
import { useMemo, useState, useEffect } from 'react'

import WaitingDoctorPane from '../components/WaitingDoctorPane'
import { useGetWaitingRoomQuery } from '@/core/services/waiting-room'
import { useGetConsultationQuery } from '@/core/services/consultation'
import { countAvailableDoctors, toDoctorIds, useRoomDoctorStatuses } from '@/hooks/useRoomDoctorStatuses'

type WaitingDoctorPaneContainerProps = {
  consultationId: string
  waitingRoomId: string
}

export default function WaitingDoctorPaneContainer(props: WaitingDoctorPaneContainerProps) {
  const { consultationId, waitingRoomId } = props

  const { data: consultation, refetch: refetchConsultation } = useGetConsultationQuery(consultationId)
  const { data: waitingRoom, refetch: refetchWaitingRoom } = useGetWaitingRoomQuery(waitingRoomId)
  const { statuses: doctorStatuses, isReady: doctorStatusesReady } = useRoomDoctorStatuses(waitingRoomId)

  const [lastUpdated, setLastUpdated] = useState<Date>(new Date())
  const [isRefreshing, setIsRefreshing] = useState(false)

  useEffect(() => {
    if (waitingRoom) {
      setLastUpdated(new Date())
    }
  }, [waitingRoom])

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await Promise.all([refetchConsultation(), refetchWaitingRoom()])
    } finally {
      setIsRefreshing(false)
    }
  }

  const { avgWaitingTime, queuePosition } = useMemo(() => {
    if (!waitingRoom || !waitingRoom.doctors.length) return { avgWaitingTime: undefined, queuePosition: undefined }

    const availableDoctors = countAvailableDoctors(toDoctorIds(waitingRoom.doctors), doctorStatuses)
    const consultationIndex = _.findIndex(waitingRoom.consultations, (consultation) => consultation._id === consultationId)

    if (consultationIndex === -1) return { avgWaitingTime: undefined, queuePosition: undefined }

    const position = consultationIndex + 1 // Convert to 1-based position

    if (availableDoctors === 0) return { avgWaitingTime: undefined, queuePosition: position }

    if (availableDoctors > consultationIndex) return { avgWaitingTime: 0, queuePosition: position }

    return {
      avgWaitingTime: waitingRoom.avgWaitingTime * (consultationIndex + 1 - availableDoctors),
      queuePosition: position,
    }
  }, [consultationId, doctorStatuses, waitingRoom])

  const noAvailableDoctors = useMemo(
    () => doctorStatusesReady && countAvailableDoctors(toDoctorIds(waitingRoom?.doctors), doctorStatuses) === 0,
    [doctorStatuses, doctorStatusesReady, waitingRoom]
  )

  if (!consultation || !waitingRoom) return null

  return (
    <WaitingDoctorPane
      avgWaitingTime={avgWaitingTime}
      isRefreshing={isRefreshing}
      lastUpdated={lastUpdated}
      noAvailableDoctors={noAvailableDoctors}
      onRefresh={handleRefresh}
      peopleAhead={queuePosition !== undefined ? queuePosition - 1 : undefined}
      reservedAt={consultation.startDate}
      waitingRoomName={waitingRoom.name}
    />
  )
}
